"use client";
import React, { useCallback } from "react";
import { cn } from "@/lib/utils";
import type { RelatedArticlesProps, Article } from "./types";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { usePrevNextButtons } from "@/components/Homepage/HomepageHero/components/HomepageHeroCarousel/CarouselArrowButtons";
import { useDotButton } from "@/components/Homepage/HomepageHero/components/HomepageHeroCarousel/CarouselDotButton";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import useTranslations from "@/i18n/useTranslations";

const carouselOptions: EmblaOptionsType = { loop: false, align: "start" };

function ArticleCard({
  title,
  description,
  author,
  date,
  url,
  onClick,
}: {
  title: string;
  description: string;
  author: string;
  date: string;
  url?: string;
  onClick?: () => void;
}) {
  const t = useTranslations("Common");
  const content = (
    <div className="flex flex-col h-full">
      <h3 className="text-lg font-semibold mb-2 !mt-0 text-gray-900">
        {title}
      </h3>
      <p className="text-sm text-gray-600 mb-4 flex-grow">{description}</p>
      <div className="text-xs text-gray-600">
        <span>
          {t("byAuthor")}{" "}
          <a href="#" className="text-blue-600 hover:underline">
            {author}
          </a>
        </span>
        <span className="mx-2">|</span>
        <span>{date}</span>
      </div>
    </div>
  );

  if (url) {
    return (
      <a
        href={url}
        onClick={onClick}
        className="block md:flex-1 hover:opacity-80 transition-opacity no-underline"
      >
        {content}
      </a>
    );
  }

  return <div className="md:flex-1">{content}</div>;
}

export default function RelatedArticles({
  heading,
  articles,
  className,
}: RelatedArticlesProps) {
  const t = useTranslations("Common");
  const resolvedHeading = heading ?? t("relatedArticles");
  const carouselMessages = useCarouselMessages();

  const useDesktopCarousel = articles.length >= 4;
  // Always use carousel - it will be configured differently for mobile vs desktop
  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOptions);

  const { elementClicked } = useElementEvents({
    webElement: {
      name: "related_article",
      elementType: "link",
      location: "related_articles_section",
    },
  });

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    // No autoplay, so this is just for consistency
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  const handleArticleClick = useCallback(
    (article: Article) => {
      elementClicked({
        webElement: {
          text: article.title,
        },
        outboundUrl: article.url || "#",
      });
    },
    [elementClicked],
  );

  // Desktop: Regular grid for 3 or fewer, carousel for 4+
  // Mobile: Always carousel (1 article at a time)
  return (
    <div className={cn("container mx-auto py-12 w-full", className)}>
      <h2 className="text-2xl font-bold mb-6 mt-0 text-gray-900">
        {resolvedHeading}
      </h2>

      {/* Desktop: Show grid with dividers for 3 or fewer articles */}
      {!useDesktopCarousel && (
        <div className="hidden md:flex md:flex-row">
          {articles.map((article, index) => (
            <React.Fragment key={index}>
              <ArticleCard
                {...article}
                onClick={() => handleArticleClick(article)}
              />
              {index < articles.length - 1 && (
                <div className="w-px bg-gray-300 mx-8" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Mobile: Always carousel (1 at a time) */}
      {/* Desktop: Carousel for 4+ articles (3 at a time) */}
      <div className={cn("relative", !useDesktopCarousel && "md:hidden")}>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-8">
            {articles.map((article, index) => (
              <React.Fragment key={index}>
                <div
                  className={cn(
                    "min-w-0 relative",
                    useDesktopCarousel
                      ? "flex-[0_0_100%] md:flex-[0_0_calc(33.333%-1.5rem)]"
                      : "flex-[0_0_100%]",
                  )}
                >
                  <ArticleCard
                    {...article}
                    onClick={() => handleArticleClick(article)}
                  />
                  {/* Vertical divider on desktop for carousel mode */}
                  {useDesktopCarousel && index < articles.length - 1 && (
                    <div className="hidden md:block absolute right-[-16px] top-0 bottom-0 w-px bg-gray-300" />
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Carousel Controls */}
        <div className="w-full px-8 mt-10 flex flex-row justify-center">
          <div className="flex flex-row justify-center w-fit items-center gap-2 bg-white rounded-full">
            <Button
              variant="ghost"
              size="icon"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              aria-label={carouselMessages.previousArticles}
              className="hover:bg-transparent"
            >
              <ChevronLeft />
            </Button>

            <div className="flex gap-6">
              {scrollSnaps.map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="icon"
                  onClick={() => onDotButtonClick(index)}
                  aria-label={carouselMessages.goToSlide(index + 1)}
                  className={`h-3 w-3 rounded-full p-0 ${
                    index === selectedIndex ? "bg-brand-500" : "bg-primary-500"
                  }`}
                >
                  <Circle
                    svgProps={{
                      width: "8",
                      height: "8",
                      className: `${index === selectedIndex ? "text-brand-500" : "text-primary-500"}`,
                    }}
                  />
                </Button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              aria-label={carouselMessages.nextArticles}
              className="hover:bg-transparent"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
